import DatePicker, {
  CalendarContainer,
  ReactDatePickerCustomDayNameProps,
  ReactDatePickerCustomHeaderProps,
} from "react-datepicker";
import React, { forwardRef, ReactNode } from "react";
import {
  DateCell,
  DateHeader,
  DateInputContainer,
  FlashPCBCalendarContainer,
  HeaderContainer,
  LegendContainer,
  LegendRow,
  LegendTitle,
} from "./FlashPCBDatePickerStyles";
import Icon from "../Icon";
import {
  isBeforeFirstShipDate,
  isClosedOnDate,
  isFastestOption,
  isFasterOption,
  isToday,
  isWeekend,
  getOption,
  FASTEST_BASE_LEAD_TIME,
  FASTER_BASE_LEAD_TIME,
} from "../../../utils/hooks/commonly-stored/leadTimeOptions";
import {
  dateInputStringToDate,
  dateToHumanReadableDate,
  dateToLocalDateString,
  fromLocalDateString,
  leadTimeInfoString,
} from "../../../utils/valueFormatting";
import { COLORS } from "../../../constants/Styles";
import { LeadTimeOptions } from "../../../reducers/uploadedBoard";
import { useAppSelector } from "../../../utils/hooks/store";

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type DatePickerInputProps = {
  className?: string;

  value?: string;
  onClick?: () => void;
  leadTimeDays: number | null;
};

const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  ({ className, value, onClick, leadTimeDays }, ref) => {
    const getValue = () => {
      if (value) {
        const date = dateInputStringToDate(value);
        if (leadTimeDays !== null) {
          return leadTimeInfoString(leadTimeDays, dateToLocalDateString(date));
        }
        return dateToHumanReadableDate(date, true);
      }
    };

    return (
      <DateInputContainer onClick={onClick} className={className} ref={ref}>
        {getValue()}
        <Icon
          src="/icons/calendar.svg"
          alt="Calendar"
          iconDimension={16}
          styleProps={{ margin: "0" }}
        />
      </DateInputContainer>
    );
  },
);
DatePickerInput.displayName = "DatePickerInput";

export function DatePickerHeader({
  date,
  decreaseMonth,
  increaseMonth,
}: ReactDatePickerCustomHeaderProps) {
  return (
    <HeaderContainer>
      <Icon
        src={"/icons/retract.svg"}
        alt="Decrease month"
        iconDimension={12}
        styleProps={{
          margin: "0.5rem",
          transform: "rotate(-90deg)",
          cursor: "pointer",
        }}
        onClick={decreaseMonth}
      />
      {MONTHS[date.getMonth()]} {date.getFullYear()}
      <Icon
        src={"/icons/retract.svg"}
        alt="Increase month"
        iconDimension={12}
        styleProps={{
          margin: "0.5rem",
          transform: "rotate(90deg)",
          cursor: "pointer",
        }}
        onClick={increaseMonth}
      />
    </HeaderContainer>
  );
}

function DayOfWeek({ shortName }: ReactDatePickerCustomDayNameProps) {
  return <DateHeader>{shortName}</DateHeader>;
}

interface DateDisplayOptions {
  backgroundColor: string;
  message: string;
}

function getDisplayOptions(
  date: Date,
  leadTimeOptions: LeadTimeOptions,
): DateDisplayOptions | null {
  if (leadTimeOptions === null) {
    return null;
  }
  const closure = isClosedOnDate(date, leadTimeOptions);
  if (closure !== null) {
    return {
      message: `Closed for ${closure}`,
      backgroundColor: COLORS.warn,
    };
  } else if (isFastestOption(date, leadTimeOptions)) {
    return {
      message: "This is our fastest pricing tier",
      backgroundColor: COLORS.fastest,
    };
  } else if (isFasterOption(date, leadTimeOptions)) {
    return {
      message: "This is our fast pricing tier",
      backgroundColor: COLORS.faster,
    };
  }
  return null;
}

export default function FlashPCBDatePicker({
  selectedDate,
  selectedLeadTime,
  setSelectedDate,
  leadTimeOptions,
}: {
  selectedDate: string | null;
  selectedLeadTime: number | null;
  setSelectedDate: (shipDate: string | null) => void;
  leadTimeOptions: LeadTimeOptions;
}) {
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );

  const handleChange = (date: Date | null) => {
    if (date !== null) {
      setSelectedDate(dateToLocalDateString(date));
    } else {
      setSelectedDate(null);
    }
  };

  const isDateSelectable = (date: Date) => {
    if (isAdminOrEngineer) {
      return true;
    }
    return (
      !isWeekend(date) &&
      !isClosedOnDate(date, leadTimeOptions) &&
      !isBeforeFirstShipDate(date, leadTimeOptions)
    );
  };

  const renderDate = (day: number, date: Date) => {
    const displayOptions = getDisplayOptions(date, leadTimeOptions);
    const selectable = isDateSelectable(date);
    return (
      <DateCell
        today={isToday(date)}
        selectable={selectable}
        selected={
          selectedDate !== null && dateToLocalDateString(date) == selectedDate
        }
        backgroundColor={displayOptions?.backgroundColor}
        title={displayOptions?.message}
      >
        {day}
      </DateCell>
    );
  };

  const LeadTimeOptionsCalendarContainer = ({
    className,
    children,
  }: {
    className: string;
    children: ReactNode;
  }) => {
    return (
      <CalendarContainer className={className}>
        <FlashPCBCalendarContainer>
          <div>{children}</div>
          <Legend leadTimeOptions={leadTimeOptions} />
        </FlashPCBCalendarContainer>
      </CalendarContainer>
    );
  };

  return (
    <DatePicker
      selected={
        selectedDate !== null ? fromLocalDateString(selectedDate) : null
      }
      showDisabledMonthNavigation={false}
      onChange={handleChange}
      customInput={<DatePickerInput leadTimeDays={selectedLeadTime} />}
      renderCustomHeader={DatePickerHeader}
      filterDate={isDateSelectable}
      renderCustomDayName={DayOfWeek}
      renderDayContents={renderDate}
      calendarContainer={LeadTimeOptionsCalendarContainer}
    />
  );
}

function Legend({ leadTimeOptions }: { leadTimeOptions: LeadTimeOptions }) {
  if (leadTimeOptions === null) {
    return null;
  }
  return (
    <LegendContainer>
      <LegendTitle>Legend</LegendTitle>
      {getOption(leadTimeOptions, FASTEST_BASE_LEAD_TIME) && (
        <LegendRow>
          <DateCell backgroundColor={COLORS.fastest} />
          <div>Our fastest pricing tier</div>
        </LegendRow>
      )}
      {getOption(leadTimeOptions, FASTER_BASE_LEAD_TIME) && (
        <LegendRow>
          <DateCell backgroundColor={COLORS.faster} />
          <div>Our faster pricing tier</div>
        </LegendRow>
      )}
      <LegendRow>
        <DateCell backgroundColor={COLORS.warn} />
        <div>Office closure</div>
      </LegendRow>
    </LegendContainer>
  );
}
